import { CheckCircle, XCircle, Clock, RotateCw } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import type { ChatCommandResult } from '@/types'

interface ChatCommandResultCardProps {
  commandName: string
  result: ChatCommandResult
  onRunAgain?: () => void
}

export function ChatCommandResultCard({ commandName, result, onRunAgain }: Readonly<ChatCommandResultCardProps>) {
  return (
    <div className="border rounded-lg overflow-hidden bg-card" data-testid="chat-command-result-card">
      <div className="px-3 py-2 border-b bg-muted/30 flex items-center gap-2">
        {result.success ? (
          <CheckCircle className="h-4 w-4 text-green-500 shrink-0" />
        ) : (
          <XCircle className="h-4 w-4 text-red-500 shrink-0" />
        )}
        <span className="font-medium text-sm">{commandName}</span>
        <Badge variant={result.success ? 'secondary' : 'destructive'} className="text-[10px]">
          {result.success ? 'Success' : 'Failed'}
        </Badge>
        <span className="text-xs text-muted-foreground ml-auto flex items-center gap-1">
          <Clock className="h-3 w-3" />
          {(result.durationMs / 1000).toFixed(1)}s
        </span>
      </div>

      {result.success && Object.keys(result.outputs).length > 0 && (
        <div className="p-3">
          <pre className="text-xs font-mono bg-muted p-2 rounded overflow-x-auto whitespace-pre-wrap">
            {JSON.stringify(result.outputs, null, 2)}
          </pre>
        </div>
      )}

      {!result.success && result.errorMessage && (
        <div className="p-3" role="alert">
          <p className="text-xs text-red-600">{result.errorMessage}</p>
        </div>
      )}

      {onRunAgain && (
        <div className="px-3 py-2 border-t flex justify-end">
          <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={onRunAgain}>
            <RotateCw className="h-3 w-3 mr-1" />
            Run Again
          </Button>
        </div>
      )}
    </div>
  )
}
